import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/SEOHead';

const values = [
  {
    icon: 'ri-time-line',
    title: 'Respect for Time',
    description:
      'We know your time is valuable. Every process we build is designed to minimize wait times and maximize efficiency — for you and your family.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Accuracy First',
    description:
      'DMV errors are costly. Our team double-checks every document before submission to ensure your transaction is processed correctly the first time.',
  },
  {
    icon: 'ri-heart-line',
    title: 'Community Commitment',
    description:
      'We\'re a Las Vegas business serving Las Vegas people. We take pride in being a trusted resource for our neighbors across Clark County.',
  },
  {
    icon: 'ri-user-star-line',
    title: 'Client-Centered Service',
    description:
      'Every client is treated as an individual, not a number. We listen, we explain, and we make sure you leave confident and informed.',
  },
  {
    icon: 'ri-lock-line',
    title: 'Integrity & Transparency',
    description:
      'No hidden fees, no surprises. We tell you exactly what to expect before we begin — and we deliver on every promise we make.',
  },
  {
    icon: 'ri-lightbulb-line',
    title: 'Continuous Improvement',
    description:
      'We constantly refine our processes, train our staff, and adopt better tools to serve you faster and more effectively each year.',
  },
];

const milestones = [
  {
    year: '2008',
    title: 'Founded in Las Vegas',
    description:
      'The Villarreal Organization, Inc was established with a vision to simplify DMV services for Nevada residents.',
  },
  {
    year: '2010',
    title: 'Speedy DMV Services™ Launched',
    description:
      'Our first brand opened its doors, offering fast vehicle registration and title transfer services to the Las Vegas community.',
  },
  {
    year: '2014',
    title: 'DMV-Xpress Services™ Launched',
    description:
      'Recognizing demand for more specialized services, we launched DMV-Xpress to handle complex transactions and VIN inspections.',
  },
  {
    year: '2018',
    title: '5,000 Clients Served',
    description:
      'A major milestone — we celebrated serving over 5,000 satisfied clients across Clark County.',
  },
  {
    year: '2021',
    title: 'Villarreal Enterprise Group Formed',
    description:
      'To better reflect our growth and dual-brand structure, we established Villarreal Enterprise Group as the official parent company.',
  },
  {
    year: '2024',
    title: '10,000+ Clients & Growing',
    description:
      'Today we\'ve served over 10,000 clients and continue to expand our services and team to meet growing demand.',
  },
];

const teamMembers = [
  {
    name: 'Carlos Villarreal',
    role: 'Founder & Chief Executive Officer',
    bio: 'With over 20 years in Nevada business operations, Carlos founded the organization with a mission to make government services accessible and stress-free for every resident.',
    image:
      'https://public.readdy.ai/ai/img_res/3f937e568347b520e316995adc5877c5.jpg',
  },
  {
    name: 'Maria Villarreal',
    role: 'Chief Operations Officer',
    bio: 'Maria oversees day-to-day operations across both brands, ensuring every client interaction meets the high standards the Villarreal name is known for.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20Hispanic%20female%20business%20executive%20in%20her%2040s%20wearing%20professional%20attire%2C%20warm%20confident%20smile%2C%20clean%20modern%20office%20background%2C%20professional%20headshot%20portrait%2C%20soft%20neutral%20lighting&width=300&height=350&seq=team-maria-001&orientation=portrait',
  },
  {
    name: 'James Ortega',
    role: 'Director of Client Services',
    bio: 'James leads our client experience team, training staff and developing the processes that keep our satisfaction ratings among the highest in the industry.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20male%20business%20manager%20in%20his%2030s%20wearing%20business%20casual%20attire%2C%20friendly%20approachable%20smile%2C%20clean%20modern%20office%20background%2C%20professional%20headshot%20portrait%2C%20neutral%20lighting&width=300&height=350&seq=team-james-001&orientation=portrait',
  },
  {
    name: 'Sofia Reyes',
    role: 'Lead Document Specialist',
    bio: 'Sofia brings 12 years of DMV expertise to every transaction. Her attention to detail and deep knowledge of Nevada regulations is unmatched.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20Latina%20female%20document%20specialist%20in%20her%2030s%20wearing%20professional%20attire%2C%20focused%20and%20competent%20expression%2C%20clean%20modern%20office%20background%2C%20professional%20headshot%20portrait%2C%20warm%20lighting&width=300&height=350&seq=team-sofia-001&orientation=portrait',
  },
];

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/about#webpage`,
        url: `${siteUrl}/about`,
        name: 'About Villarreal Enterprise Group | Las Vegas DMV Services Company',
        description: 'Learn about Villarreal Enterprise Group — the Las Vegas-based parent company of Speedy DMV Services™ and DMV-Xpress Services™. Our story, values, team, and 15+ years of serving Clark County.',
        isPartOf: { '@id': `${siteUrl}/#website` },
        about: { '@id': `${siteUrl}/#organization` },
        breadcrumb: { '@id': `${siteUrl}/about#breadcrumb` },
        dateModified: '2026-04-11',
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${siteUrl}/about#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'About Us', item: `${siteUrl}/about` },
        ],
      },
      {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: 'Villarreal Enterprise Group',
        legalName: 'The Villarreal Organization, Inc',
        alternateName: ['Speedy DMV Services', 'DMV-Xpress Services'],
        url: siteUrl,
        foundingDate: '2008',
        foundingLocation: { '@type': 'Place', name: 'Las Vegas, Nevada' },
        numberOfEmployees: { '@type': 'QuantitativeValue', minValue: 10, maxValue: 50 },
        description: 'Nevada-based business group operating Speedy DMV Services™ and DMV-Xpress Services™, providing comprehensive DMV and document services in Las Vegas since 2008.',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Las Vegas',
          addressRegion: 'NV',
          postalCode: '89101',
          addressCountry: 'US',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'info@villarrealgroup.com',
          availableLanguage: ['English', 'Spanish'],
        },
        member: [
          {
            '@type': 'Person',
            name: 'Carlos Villarreal',
            jobTitle: 'Founder & Chief Executive Officer',
            worksFor: { '@id': `${siteUrl}/#organization` },
          },
          {
            '@type': 'Person',
            name: 'Maria Villarreal',
            jobTitle: 'Chief Operations Officer',
            worksFor: { '@id': `${siteUrl}/#organization` },
          },
          {
            '@type': 'Person',
            name: 'James Ortega',
            jobTitle: 'Director of Client Services',
            worksFor: { '@id': `${siteUrl}/#organization` },
          },
          {
            '@type': 'Person',
            name: 'Sofia Reyes',
            jobTitle: 'Lead Document Specialist',
            worksFor: { '@id': `${siteUrl}/#organization` },
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="About Villarreal Enterprise Group | Las Vegas DMV Services Company"
        description="Learn about Villarreal Enterprise Group — the Las Vegas-based parent company of Speedy DMV Services™ and DMV-Xpress Services™. Our story, values, team, and 15+ years of serving Clark County."
        keywords="about Villarreal Enterprise Group, Las Vegas DMV company history, Nevada DMV services team, Clark County DMV business"
        canonical={`${siteUrl}/about`}
        schema={schema}
        schemaId="schema-about"
      />
      <Header />

      {/* Hero */}
      <section className="relative bg-gray-900 py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Professional%20business%20team%20gathered%20around%20a%20conference%20table%20in%20a%20modern%20Las%20Vegas%20office%2C%20diverse%20group%20of%20professionals%20collaborating%2C%20warm%20interior%20lighting%2C%20corporate%20environment%20with%20large%20windows%20showing%20city%20view&width=1400&height=500&seq=about-hero-001&orientation=landscape"
            alt="About Villarreal Enterprise Group"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/85 to-gray-900/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">About Us</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            About Us
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl leading-relaxed">
            We're a Nevada-born business group with one mission: make essential services simpler, 
            faster, and more human for every client we serve.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2 mb-6">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-history-line text-gray-700 text-sm"></i>
                </div>
                <span className="text-sm font-medium text-gray-700">Our Story</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Built in Las Vegas. Built for Las Vegas.
              </h2>
              <div className="space-y-5 text-gray-600 leading-relaxed">
                <p>
                  Villarreal Enterprise Group was born from a simple frustration: DMV processes in Nevada were 
                  unnecessarily complicated, time-consuming, and stressful for everyday residents. Our founder, 
                  Carlos Villarreal, saw an opportunity to change that.
                </p>
                <p>
                  Starting with a single office and a small team, we built Speedy DMV Services™ from the ground up — 
                  focused entirely on speed, accuracy, and treating every client like a neighbor. The response from 
                  the Las Vegas community was overwhelming.
                </p>
                <p>
                  As demand grew for more specialized services, we launched DMV-Xpress Services™ to handle complex 
                  transactions that required deeper expertise. Today, both brands operate under the Villarreal 
                  Enterprise Group umbrella, serving thousands of clients annually across Clark County.
                </p>
                <p className="text-sm text-gray-500 italic">
                  Operated by The Villarreal Organization, Inc — a Nevada-registered corporation.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20DMV%20service%20office%20exterior%20with%20clean%20signage%20and%20welcoming%20entrance%2C%20Las%20Vegas%20Nevada%20commercial%20building%2C%20daytime%20clear%20sky%2C%20professional%20business%20appearance&width=350&height=280&seq=about-story-1&orientation=landscape"
                alt="Our office"
                className="w-full h-48 object-cover object-top rounded-xl"
              />
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20staff%20member%20helping%20a%20customer%20with%20vehicle%20registration%20documents%20at%20a%20clean%20modern%20desk%2C%20friendly%20interaction%2C%20organized%20office%20environment%2C%20warm%20lighting&width=350&height=280&seq=about-story-2&orientation=landscape"
                alt="Serving clients"
                className="w-full h-48 object-cover object-top rounded-xl mt-8"
              />
              <img
                src="https://readdy.ai/api/search-image?query=Team%20of%20professional%20DMV%20service%20employees%20in%20a%20group%20photo%2C%20diverse%20friendly%20staff%2C%20clean%20office%20background%2C%20professional%20attire%2C%20warm%20smiles&width=350&height=280&seq=about-story-3&orientation=landscape"
                alt="Our team"
                className="w-full h-48 object-cover object-top rounded-xl"
              />
              <img
                src="https://readdy.ai/api/search-image?query=Las%20Vegas%20Nevada%20cityscape%20with%20professional%20business%20district%20buildings%2C%20daytime%20clear%20sky%2C%20modern%20urban%20environment%2C%20Nevada%20desert%20landscape%20in%20background&width=350&height=280&seq=about-story-4&orientation=landscape"
                alt="Las Vegas"
                className="w-full h-48 object-cover object-top rounded-xl mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gray-50 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '10,000+', label: 'Clients Served', icon: 'ri-user-heart-line' },
              { value: '15+', label: 'Years in Business', icon: 'ri-calendar-line' },
              { value: '2', label: 'Trusted Brands', icon: 'ri-store-2-line' },
              { value: '4.8★', label: 'Average Rating', icon: 'ri-star-line' },
            ].map((stat, i) => (
              <div key={i}>
                <div className="w-12 h-12 flex items-center justify-center bg-gray-200 rounded-full mx-auto mb-3">
                  <i className={`${stat.icon} text-xl text-gray-700`}></i>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What We Stand For</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These aren't just words on a wall — they're the principles that guide every decision we make.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((val, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center bg-gray-200 rounded-lg mb-4">
                  <i className={`${val.icon} text-xl text-gray-800`}></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{val.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{val.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-gray-600">From a single office to a multi-brand enterprise — here's how we got here.</p>
          </div>
          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-gray-300 hidden sm:block"></div>
            <div className="space-y-10">
              {milestones.map((m, i) => (
                <div key={i} className="flex flex-col sm:flex-row gap-4 sm:gap-8">
                  <div className="flex items-center gap-4 sm:flex-col sm:items-center sm:w-16 shrink-0">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-900 text-white rounded-full text-xs font-bold shrink-0 z-10">
                      {m.year.slice(2)}
                    </div>
                    <span className="text-sm font-bold text-gray-900 sm:hidden">{m.year}</span>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200 flex-1">
                    <div className="text-xs font-semibold text-gray-400 mb-1 hidden sm:block">{m.year}</div>
                    <h3 className="font-bold text-gray-900 mb-2">{m.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{m.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Meet the Leadership Team</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The people behind the brands — experienced, dedicated, and passionate about serving our community.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, i) => (
              <div key={i} className="text-center">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-64 object-cover object-top rounded-xl mb-4"
                />
                <h3 className="font-bold text-gray-900 mb-1">{member.name}</h3>
                <p className="text-sm text-gray-500 mb-3">{member.role}</p>
                <p className="text-xs text-gray-600 leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to work with us?</h2>
          <p className="text-gray-400 mb-8 leading-relaxed">
            Whether you need a simple registration renewal or a complex title transfer, 
            our team is ready to help. Reach out today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line"></i>
              Contact Us
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-2 bg-white/10 text-white border border-white/20 px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-list-check-2"></i>
              View Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
